import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  ClipboardList,
  Eye,
  AlertTriangle,
  MousePointer2,
  CalendarRange,
  Download,
  LayoutList,
  Layers,
  Loader2,
  RefreshCw,
  Car,
  MapPin,
  Wrench,
  Fuel,
  CheckCircle2,
  Clock3,
  FileCheck2,
  FileWarning,
  TimerReset,
  BadgeCheck,
  User,
  Gauge,
  Calendar,
  Activity,
  FileText,
} from 'lucide-react';
import { format, isValid, parseISO, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'jspdf-autotable';
import type { jsPDF } from 'jspdf';
import { employeeService, type Employee } from '../../api/Employee';
import { fuelingService, type Fueling } from '../../api/fuelings';
import { incidentService, type Incident } from '../../api/incidents';
import { maintenanceService, type Maintenance } from '../../api/maintenance';
import { reportService, type Report } from '../../api/report';
import { tripService, type Trip } from '../../api/trips';
import { vehicleService, type Vehicle } from '../../api/vehicles';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  Button,
  showToast,
} from '../../components/ui/SharedLayout';

type ReportPeriod = '7days' | '30days' | '90days' | 'custom';
type ReportView = 'overview' | 'detailed';
type ReportTab = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface DateRangeState {
  startDate: Date;
  endDate: Date;
}

const DATE_FORMAT = 'dd/MM/yyyy';
const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const tabs = [
  'Visão Geral',
  'Veículos',
  'Viagens',
  'Combustível',
  'Manutenções',
  'Incidentes',
  'Relatórios Pós-Uso',
] as const;

const formatTripEndDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

const formatTripMinutes = (minutes?: number) => {
  if (!minutes) return '-';
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (!hours) return `${rem} min`;
  if (!rem) return `${hours}h`;
  return `${hours}h ${rem}min`;
};

const tripVehicleLabel = (trip: Trip) => {
  if (!trip.vehicle) return `Veículo #${trip.vehicle_id}`;
  const primary =
    trip.vehicle.brand?.trim() ||
    trip.vehicle.description?.trim() ||
    'Veículo';
  return `${primary} - ${trip.vehicle.license_plate ?? `#${trip.vehicle_id}`}`;
};

const parseDateValue = (value?: string | null): Date | null => {
  if (!value) return null;

  const parsed = parseISO(value);
  if (isValid(parsed)) return parsed;

  const fallback = new Date(value);
  return isValid(fallback) ? fallback : null;
};

const formatDateValue = (value?: string | Date | null) => {
  if (!value) return '-';
  if (value instanceof Date) return format(value, DATE_FORMAT, { locale: ptBR });
  const parsed = parseDateValue(value);
  return parsed ? format(parsed, DATE_FORMAT, { locale: ptBR }) : '-';
};

const isWithinDateRange = (value: string | undefined, range: DateRangeState) => {
  const parsed = parseDateValue(value);
  if (!parsed) return false;
  const normalized = new Date(parsed);
  normalized.setHours(0, 0, 0, 0);
  const start = new Date(range.startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(range.endDate);
  end.setHours(23, 59, 59, 999);
  return normalized >= start && normalized <= end;
};

const formatCurrency = (value: number) => currencyFormatter.format(value || 0);

const getTripDate = (trip: Trip) => trip.start_date || trip.created_at;

const getVehicleLabel = (vehicle?: Vehicle) =>
  vehicle ? `${vehicle.brand} - ${vehicle.license_plate}` : '-';

const getStatusChipClass = (status: string) => {
  switch (status) {
    case 'disponivel':
    case 'concluida':
    case 'concluded':
    case 'ativo':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20';
    case 'em_andamento':
    case 'em_uso':
    case 'aprovada':
    case 'active':
      return 'bg-blue-50 text-blue-700 ring-blue-600/20';
    case 'pendente':
    case 'pending':
      return 'bg-amber-50 text-amber-700 ring-amber-600/20';
    case 'manutencao':
    case 'rejeitada':
    case 'cancelada':
    case 'rejected':
    case 'inativo':
      return 'bg-rose-50 text-rose-700 ring-rose-600/20';
    default:
      return 'bg-slate-50 text-slate-700 ring-slate-500/15';
  }
};

const maintenanceStatusLabelMap: Record<Maintenance['status'], string> = {
  pending: 'Pendente',
  active: 'Aprovada',
  rejected: 'Rejeitada',
  concluded: 'Concluída',
};

const formatVehicleStatus = (status?: string) => {
  switch (status) {
    case 'disponivel':
      return 'Disponível';
    case 'manutencao':
      return 'Em manutenção';
    case 'em_uso':
      return 'Em uso';
    default:
      return status || '-';
  }
};

const formatTripStatus = (status?: string) => {
  switch (status) {
    case 'pendente':
      return 'Pendente';
    case 'aprovada':
      return 'Aprovada';
    case 'em_andamento':
      return 'Em andamento';
    case 'concluida':
      return 'Concluída';
    case 'rejeitada':
      return 'Rejeitada';
    case 'cancelada':
      return 'Cancelada';
    default:
      return status || '-';
  }
};

const EmptyState = ({ message }: { message: string }) => (
  <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
    {message}
  </div>
);

const ClientRelatorios = () => {
  const { setPageTitle } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<ReportPeriod>('30days');
  const [view, setView] = useState<ReportView>('overview');
  const [activeTab, setActiveTab] = useState<ReportTab>(0);
  const [dateRange, setDateRange] = useState<DateRangeState>({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
  });
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [fuelings, setFuelings] = useState<Fueling[]>([]);
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [postUseReports, setPostUseReports] = useState<Report[]>([]);
  const [selectedPostUseTripId, setSelectedPostUseTripId] = useState<number | null>(null);

  useEffect(() => {
    setPageTitle('Relatórios do setor');
  }, [setPageTitle]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [
          loadedTrips,
          loadedVehicles,
          loadedEmployees,
          loadedFuelings,
          loadedMaintenances,
          loadedIncidents,
          loadedPostUseReports,
        ] = await Promise.all([
          tripService.getAll(),
          vehicleService.getAllVehicles(),
          employeeService.getAllEmployees(),
          fuelingService.getAllFuelings(),
          maintenanceService.getByStatuses([
            'pending',
            'active',
            'rejected',
            'concluded',
          ]),
          incidentService.getAll(),
          reportService.getAll(),
        ]);

        setTrips(Array.isArray(loadedTrips) ? loadedTrips : []);
        setVehicles(Array.isArray(loadedVehicles) ? loadedVehicles : []);
        setEmployees(Array.isArray(loadedEmployees) ? loadedEmployees : []);
        setFuelings(Array.isArray(loadedFuelings) ? loadedFuelings : []);
        setMaintenances(Array.isArray(loadedMaintenances) ? loadedMaintenances : []);
        setIncidents(Array.isArray(loadedIncidents) ? loadedIncidents : []);
        setPostUseReports(
          Array.isArray(loadedPostUseReports)
            ? loadedPostUseReports.filter((r) => !!r.trip_id)
            : [],
        );
      } catch (err) {
        console.error('Error loading report data:', err);
        setError('Erro ao carregar os dados do relatório. Por favor, tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const vehicleMap = useMemo(
    () => new Map(vehicles.map((vehicle) => [vehicle.id, vehicle])),
    [vehicles],
  );

  const employeeMap = useMemo(
    () => new Map(employees.map((employee) => [employee.id, employee])),
    [employees],
  );

  const concludedTripsInRange = useMemo(
    () =>
      trips
        .filter((trip) => trip.status === 'concluida')
        .filter((trip) => isWithinDateRange(trip.end_date || trip.updated_at, dateRange))
        .sort((a, b) => {
          const dateA = new Date(a.end_date || a.updated_at).getTime();
          const dateB = new Date(b.end_date || b.updated_at).getTime();
          return dateB - dateA;
        }),
    [trips, dateRange],
  );

  const postUseReportsByTripId = useMemo(
    () =>
      new Map(
        postUseReports
          .filter((r): r is Report & { trip_id: number } => !!r.trip_id)
          .map((r) => [r.trip_id, r]),
      ),
    [postUseReports],
  );

  const selectedPostUseTrip = useMemo(
    () => concludedTripsInRange.find((t) => t.id === selectedPostUseTripId) || null,
    [concludedTripsInRange, selectedPostUseTripId],
  );

  const selectedPostUseReport = useMemo(
    () => (selectedPostUseTrip ? postUseReportsByTripId.get(selectedPostUseTrip.id) || null : null),
    [postUseReportsByTripId, selectedPostUseTrip],
  );

  const filteredTrips = useMemo(
    () => trips.filter((trip) => isWithinDateRange(getTripDate(trip), dateRange)),
    [trips, dateRange],
  );

  const filteredFuelings = useMemo(
    () =>
      fuelings.filter((fueling) =>
        isWithinDateRange(fueling.fueling_time || fueling.created_at, dateRange),
      ),
    [fuelings, dateRange],
  );

  const filteredMaintenances = useMemo(
    () =>
      maintenances.filter((maintenance) =>
        isWithinDateRange(maintenance.entry_date || maintenance.created_at, dateRange),
      ),
    [maintenances, dateRange],
  );

  const filteredIncidents = useMemo(
    () => incidents.filter((incident) => isWithinDateRange(incident.date, dateRange)),
    [incidents, dateRange],
  );

  const tripCountByVehicle = useMemo(() => {
    const counts = new Map<number, number>();
    filteredTrips.forEach((trip) => {
      counts.set(trip.vehicle_id, (counts.get(trip.vehicle_id) || 0) + 1);
    });
    return counts;
  }, [filteredTrips]);

  const fuelingCountByVehicle = useMemo(() => {
    const counts = new Map<number, number>();
    filteredFuelings.forEach((fueling) => {
      counts.set(fueling.vehicle_id, (counts.get(fueling.vehicle_id) || 0) + 1);
    });
    return counts;
  }, [filteredFuelings]);

  const lastTripDateByVehicle = useMemo(() => {
    const dates = new Map<number, string>();
    filteredTrips.forEach((trip) => {
      const current = dates.get(trip.vehicle_id);
      const nextValue = getTripDate(trip);
      if (!current || parseDateValue(nextValue)! > parseDateValue(current)!) {
        dates.set(trip.vehicle_id, nextValue);
      }
    });
    return dates;
  }, [filteredTrips]);

  const stats = useMemo(
    () => ({
      totalVehicles: vehicles.length,
      activeVehicles: vehicles.filter((vehicle) => vehicle.status === 'disponivel').length,
      totalTrips: filteredTrips.length,
      activeTrips: filteredTrips.filter((trip) => trip.status === 'em_andamento').length,
      totalFuelCost: filteredFuelings.reduce((sum, fueling) => sum + (fueling.total_cost || 0), 0),
      totalFuelVolume: filteredFuelings.reduce((sum, fueling) => sum + (fueling.amount || 0), 0),
      totalMaintenanceCost: filteredMaintenances.reduce(
        (sum, maintenance) => sum + (maintenance.final_price || 0),
        0,
      ),
      openMaintenances: filteredMaintenances.filter(
        (maintenance) => maintenance.status === 'pending' || maintenance.status === 'active',
      ).length,
      totalIncidents: filteredIncidents.length,
      activeEmployees: employees.filter((employee) => employee.status === 'ativo').length,
    }),
    [vehicles, filteredTrips, filteredFuelings, filteredMaintenances, filteredIncidents, employees],
  );

  const overviewCards = useMemo(
    () => [
      {
        label: 'Veículos do setor',
        value: `${stats.activeVehicles} / ${stats.totalVehicles}`,
        helper:
          stats.totalVehicles > 0
            ? `${Math.round((stats.activeVehicles / stats.totalVehicles) * 100)}% disponíveis`
            : 'Nenhum veículo no departamento',
      },
      {
        label: 'Viagens do período',
        value: `${stats.activeTrips} / ${stats.totalTrips}`,
        helper:
          stats.totalTrips > 0
            ? `${Math.round((stats.activeTrips / stats.totalTrips) * 100)}% em andamento`
            : 'Nenhuma viagem no período',
      },
      {
        label: 'Combustível',
        value: formatCurrency(stats.totalFuelCost),
        helper: `${stats.totalFuelVolume.toFixed(1)} L abastecidos no período`,
      },
      {
        label: 'Manutenções',
        value: formatCurrency(stats.totalMaintenanceCost),
        helper: `${stats.openMaintenances} pendentes e ${stats.totalIncidents} incidentes registrados`,
      },
    ],
    [stats],
  );

  const topVehicles = useMemo(() => {
    return vehicles
      .map((vehicle) => ({
        ...vehicle,
        tripCount: tripCountByVehicle.get(vehicle.id) || 0,
        fuelingCount: fuelingCountByVehicle.get(vehicle.id) || 0,
        lastTripDate: lastTripDateByVehicle.get(vehicle.id),
      }))
      .filter((vehicle) => vehicle.tripCount > 0 || vehicle.fuelingCount > 0)
      .sort((a, b) => b.tripCount - a.tripCount || b.fuelingCount - a.fuelingCount)
      .slice(0, 5);
  }, [vehicles, tripCountByVehicle, fuelingCountByVehicle, lastTripDateByVehicle]);

  const recentTrips = useMemo(
    () =>
      [...filteredTrips]
        .sort(
          (a, b) =>
            (parseDateValue(getTripDate(b))?.getTime() || 0) -
            (parseDateValue(getTripDate(a))?.getTime() || 0),
        )
        .slice(0, 5),
    [filteredTrips],
  );

  const recentIncidents = useMemo(
    () =>
      [...filteredIncidents]
        .sort(
          (a, b) =>
            (parseDateValue(b.date)?.getTime() || 0) - (parseDateValue(a.date)?.getTime() || 0),
        )
        .slice(0, 5),
    [filteredIncidents],
  );

  const handlePeriodChange = (newPeriod: ReportPeriod) => {
    setPeriod(newPeriod);
    const today = new Date();
    let startDate = new Date();

    switch (newPeriod) {
      case '7days':
        startDate = subDays(today, 7);
        break;
      case '30days':
        startDate = subDays(today, 30);
        break;
      case '90days':
        startDate = subDays(today, 90);
        break;
      case 'custom':
        return;
    }

    setDateRange({ startDate, endDate: today });
  };

  const formatDateForInput = (date: Date) => format(date, 'yyyy-MM-dd');

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'start' | 'end') => {
    const date = e.target.value ? new Date(`${e.target.value}T00:00:00`) : new Date();
    if (type === 'start') {
      setDateRange((prev) => ({ ...prev, startDate: date }));
    } else {
      setDateRange((prev) => ({ ...prev, endDate: date }));
    }
  };

  const addHeader = (doc: jsPDF, pageNumber: number) => {
    doc.setFontSize(18);
    doc.setTextColor(40, 62, 80);
    doc.text('Relatório de Atividades do Setor', 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text(`Página ${pageNumber}`, 190, 20, { align: 'right' });
    doc.setDrawColor(200);
    doc.line(14, 25, 200, 25);
    doc.setTextColor(0);
  };

  const generatePDF = async () => {
    try {
      const { default: JsPDFConstructor } = await import('jspdf');
      const autoTableImport = await import('jspdf-autotable');
      const doc = new JsPDFConstructor({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const autoTable = autoTableImport.autoTable || (autoTableImport as unknown as { default: any }).default;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const addAutoTable = (options: any) => autoTable(doc, options);
      let pageNumber = 1;

      addHeader(doc, pageNumber);

      const dateRangeText = `${formatDateValue(dateRange.startDate)} a ${formatDateValue(dateRange.endDate)}`;
      const generatedDate = format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });

      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Período: ${dateRangeText}`, 14, 35);
      doc.text(`Gerado em: ${generatedDate}`, 14, 41);

      doc.setFontSize(14);
      doc.setTextColor(40, 62, 80);
      doc.text('Resumo do Período', 14, 58);

      addAutoTable({
        startY: 64,
        head: [['Métrica', 'Valor']],
        body: [
          ['Veículos Ativos/Total', `${stats.activeVehicles}/${stats.totalVehicles}`],
          ['Viagens em Andamento/Total', `${stats.activeTrips}/${stats.totalTrips}`],
          ['Custo de Combustível', formatCurrency(stats.totalFuelCost)],
          ['Custo de Manutenção', formatCurrency(stats.totalMaintenanceCost)],
          ['Incidentes', stats.totalIncidents.toString()],
          ['Funcionários Ativos', stats.activeEmployees.toString()],
        ],
        theme: 'grid',
        headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold' },
      });

      doc.addPage();
      pageNumber += 1;
      addHeader(doc, pageNumber);
      doc.setFontSize(14);
      doc.setTextColor(40, 62, 80);
      doc.text('Veículos com Mais Utilização', 14, 35);

      addAutoTable({
        startY: 42,
        head: [['Veículo', 'Departamento', 'Viagens', 'Última viagem']],
        body:
          topVehicles.length > 0
            ? topVehicles.map((vehicle) => [
                `${vehicle.brand} - ${vehicle.license_plate}`,
                vehicle.department_name || '-',
                vehicle.tripCount,
                formatDateValue(vehicle.lastTripDate),
              ])
            : [['Sem dados', '-', '-', '-']],
        theme: 'grid',
        headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold' },
      });

      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i += 1) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          'Gerado por SuaFrota - Sistema de Gestão de Frotas',
          105,
          287,
          { align: 'center' },
        );
      }

      doc.save(`relatorio-setor-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (pdfError) {
      console.error('Erro ao gerar PDF:', pdfError);
      showToast({
        type: 'error',
        title: 'Não foi possível gerar o PDF',
        description: 'Tente novamente em alguns segundos. Se o erro persistir, contate o suporte.',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-5 px-4 py-10">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-blue-500/20 blur-xl" />
          <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_-10px_rgba(37,99,235,0.35)] ring-1 ring-inset ring-white">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600 stroke-[1.6]" />
          </div>
        </div>
        <div className="space-y-1 text-center">
          <h2 className="text-lg font-bold tracking-tight text-slate-800">
            Carregando dados dos relatórios do setor
          </h2>
          <p className="text-sm font-medium text-slate-500">
            Estamos preparando suas informações do departamento...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-12">
        <div className="overflow-hidden rounded-2xl border border-rose-200 bg-white shadow-[0_10px_25px_-12px_rgba(244,63,94,0.2)]">
          <div className="flex items-start gap-4 border-b border-rose-100 bg-gradient-to-r from-rose-50 to-white px-6 py-5 ring-1 ring-inset ring-white">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-rose-100 text-rose-600 ring-1 ring-inset ring-rose-200/60 shadow-[0_2px_8px_-2px_rgba(244,63,94,0.15)]">
              <AlertTriangle className="h-6 w-6 stroke-[1.9]" />
            </div>
            <div className="flex-1 pt-0.5">
              <h3 className="text-base font-bold tracking-tight text-slate-900">
                Falha ao carregar relatórios
              </h3>
              <p className="mt-1 text-sm font-medium leading-snug text-slate-600">
                Não foi possível conectar ao servidor neste momento. Verifique sua conexão e tente novamente.
              </p>
            </div>
          </div>
          <div className="space-y-3 bg-white/90 px-6 py-5">
            <div className="rounded-xl border border-rose-200/60 bg-rose-50/60 px-4 py-3 ring-1 ring-inset ring-rose-100/80">
              <p className="text-[13px] font-bold leading-snug text-rose-800">{error}</p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
              <Button type="button" onClick={() => window.location.reload()} variant="primary">
                <RefreshCw className="h-4 w-4 stroke-[1.9]" />
                Tentar novamente
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderOverviewTab = () => (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)] ring-1 ring-inset ring-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-12px_rgba(15,23,42,0.2)]">
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-slate-100/70 transition-all duration-200 group-hover:bg-slate-100" />
          <div className="relative">
            <div className="flex items-center justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 text-white shadow-[0_4px_14px_-4px_rgba(51,65,85,0.5)] ring-1 ring-inset ring-white/25">
                <Car className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <span className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-600 ring-1 ring-inset ring-slate-200">
                Setor
              </span>
            </div>
            <p className="mt-4 text-[12.5px] font-semibold leading-tight text-slate-500">
              {overviewCards[0].label}
            </p>
            <p className="mt-1.5 text-2xl font-bold leading-tight tracking-tight text-slate-900">
              {overviewCards[0].value}
            </p>
            <p className="mt-2.5 text-[12px] font-medium leading-snug text-slate-600">
              {overviewCards[0].helper}
            </p>
          </div>
        </div>

        <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)] ring-1 ring-inset ring-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-12px_rgba(37,99,235,0.25)]">
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-blue-50 transition-all duration-200 group-hover:bg-blue-100/60" />
          <div className="relative">
            <div className="flex items-center justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)] ring-1 ring-inset ring-white/25">
                <MapPin className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-blue-700 ring-1 ring-inset ring-blue-200">
                Período
              </span>
            </div>
            <p className="mt-4 text-[12.5px] font-semibold leading-tight text-slate-500">
              {overviewCards[1].label}
            </p>
            <p className="mt-1.5 text-2xl font-bold leading-tight tracking-tight text-slate-900">
              {overviewCards[1].value}
            </p>
            <p className="mt-2.5 text-[12px] font-medium leading-snug text-slate-600">
              {overviewCards[1].helper}
            </p>
          </div>
        </div>

        <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)] ring-1 ring-inset ring-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-12px_rgba(168,85,247,0.25)]">
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-purple-50 transition-all duration-200 group-hover:bg-purple-100/60" />
          <div className="relative">
            <div className="flex items-center justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white shadow-[0_4px_14px_-4px_rgba(168,85,247,0.55)] ring-1 ring-inset ring-white/25">
                <Fuel className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-purple-700 ring-1 ring-inset ring-purple-200">
                Abastec.
              </span>
            </div>
            <p className="mt-4 text-[12.5px] font-semibold leading-tight text-slate-500">
              {overviewCards[2].label}
            </p>
            <p className="mt-1.5 text-2xl font-bold leading-tight tracking-tight text-slate-900">
              {overviewCards[2].value}
            </p>
            <p className="mt-2.5 text-[12px] font-medium leading-snug text-slate-600">
              {overviewCards[2].helper}
            </p>
          </div>
        </div>

        <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)] ring-1 ring-inset ring-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-12px_rgba(217,119,6,0.28)]">
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-50 transition-all duration-200 group-hover:bg-amber-100/70" />
          <div className="relative">
            <div className="flex items-center justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-[0_4px_14px_-4px_rgba(217,119,6,0.55)] ring-1 ring-inset ring-white/25">
                <Wrench className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-amber-800 ring-1 ring-inset ring-amber-200">
                Oficina
              </span>
            </div>
            <p className="mt-4 text-[12.5px] font-semibold leading-tight text-slate-500">
              {overviewCards[3].label}
            </p>
            <p className="mt-1.5 text-2xl font-bold leading-tight tracking-tight text-slate-900">
              {overviewCards[3].value}
            </p>
            <p className="mt-2.5 text-[12px] font-medium leading-snug text-slate-600">
              {overviewCards[3].helper}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              Veículos mais utilizados (setor)
            </h3>
            <span className="text-sm text-slate-500">Período atual</span>
          </div>
          {topVehicles.length === 0 ? (
            <EmptyState message="Nenhuma utilização registrada no período selecionado." />
          ) : (
            <div className="space-y-3">
              {topVehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-4 py-3"
                >
                  <div>
                    <p className="font-medium text-slate-800">{getVehicleLabel(vehicle)}</p>
                    <p className="text-sm text-slate-500">
                      {vehicle.department_name || 'Sem departamento'}
                    </p>
                  </div>
                  <div className="text-right text-sm text-slate-600">
                    <p>{vehicle.tripCount} viagens</p>
                    <p>{vehicle.fuelingCount} abastecimentos</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">Viagens recentes</h3>
            <span className="text-sm text-slate-500">{filteredTrips.length} no período</span>
          </div>
          {recentTrips.length === 0 ? (
            <EmptyState message="Nenhuma viagem encontrada no período selecionado." />
          ) : (
            <div className="space-y-3">
              {recentTrips.map((trip) => (
                <div key={trip.id} className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-slate-800">
                      {trip.destination || trip.purpose || `Viagem #${trip.id}`}
                    </p>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${getStatusChipClass(trip.status)}`}
                    >
                      {formatTripStatus(trip.status)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    {getVehicleLabel(vehicleMap.get(trip.vehicle_id))} •{' '}
                    {employeeMap.get(trip.employee_id)?.name || 'Funcionário não encontrado'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Início em {formatDateValue(getTripDate(trip))}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {view === 'detailed' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">Ocorrências recentes</h3>
            <span className="text-sm text-slate-500">
              {filteredIncidents.length} incidentes e {filteredMaintenances.length} manutenções
            </span>
          </div>
          {recentIncidents.length === 0 && filteredMaintenances.length === 0 ? (
            <EmptyState message="Nenhuma ocorrência registrada no período selecionado." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Incidentes
                </h4>
                {recentIncidents.length === 0 ? (
                  <EmptyState message="Nenhum incidente no período." />
                ) : (
                  recentIncidents.map((incident) => (
                    <div
                      key={incident.id}
                      className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3"
                    >
                      <p className="font-medium text-slate-800">
                        {getVehicleLabel(vehicleMap.get(incident.vehicle_id))}
                      </p>
                      <p className="text-sm text-slate-500">
                        {incident.location || 'Local não informado'} •{' '}
                        {formatDateValue(incident.date)}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">{incident.description}</p>
                    </div>
                  ))
                )}
              </div>
              <div className="space-y-3">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Manutenções
                </h4>
                {filteredMaintenances.length === 0 ? (
                  <EmptyState message="Nenhuma manutenção no período." />
                ) : (
                  filteredMaintenances.slice(0, 5).map((maintenance) => (
                    <div
                      key={maintenance.id}
                      className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-slate-800">
                          {getVehicleLabel(vehicleMap.get(maintenance.vehicle_id))}
                        </p>
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusChipClass(maintenance.status)}`}
                        >
                          {maintenanceStatusLabelMap[maintenance.status]}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">
                        Entrada em {formatDateValue(maintenance.entry_date)}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">{maintenance.reason}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderVehiclesTab = () => (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <h3 className="text-lg font-semibold text-slate-800">Situação dos veículos do setor</h3>
      </div>
      {vehicles.length === 0 ? (
        <div className="p-5">
          <EmptyState message="Nenhum veículo cadastrado no seu departamento." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Veículo</th>
                <th className="px-4 py-3 font-medium">Departamento</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Quilometragem</th>
                <th className="px-4 py-3 font-medium">Viagens no período</th>
                <th className="px-4 py-3 font-medium">Última viagem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {vehicles.map((vehicle) => (
                <tr key={vehicle.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{getVehicleLabel(vehicle)}</div>
                    <div className="text-xs text-slate-500">
                      {vehicle.description || 'Sem descrição'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{vehicle.department_name || '-'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusChipClass(vehicle.status)}`}
                    >
                      {formatVehicleStatus(vehicle.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {typeof vehicle.mileage === 'number' ? `${vehicle.mileage} km` : '-'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {tripCountByVehicle.get(vehicle.id) || 0}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateValue(lastTripDateByVehicle.get(vehicle.id))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderTripsTab = () => (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)]">
      <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50/80 to-white px-5 py-4 ring-1 ring-inset ring-white/60">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
            <MapPin className="h-[17px] w-[17px] stroke-[1.9]" />
          </div>
          <div>
            <h3 className="text-[14px] font-bold tracking-tight text-slate-900">
              Viagens no período (setor)
            </h3>
          </div>
        </div>
      </div>
      {filteredTrips.length === 0 ? (
        <div className="p-5">
          <EmptyState message="Nenhuma viagem encontrada para o período selecionado." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Destino</th>
                <th className="px-4 py-3 font-medium">Veículo</th>
                <th className="px-4 py-3 font-medium">Funcionário</th>
                <th className="px-4 py-3 font-medium">Início</th>
                <th className="px-4 py-3 font-medium">Fim</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filteredTrips.map((trip) => (
                <tr key={trip.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">
                      {trip.destination || trip.purpose || `Viagem #${trip.id}`}
                    </div>
                    <div className="text-xs text-slate-500">
                      {trip.purpose || 'Sem finalidade informada'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {getVehicleLabel(vehicleMap.get(trip.vehicle_id))}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {employeeMap.get(trip.employee_id)?.name || 'Funcionário não encontrado'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDateValue(getTripDate(trip))}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDateValue(trip.end_date)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${getStatusChipClass(trip.status)}`}
                    >
                      {formatTripStatus(trip.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderFuelingsTab = () => (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)]">
      <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50/80 to-white px-5 py-4 ring-1 ring-inset ring-white/60">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-fuchsia-50 text-fuchsia-700 ring-1 ring-inset ring-fuchsia-200">
            <Fuel className="h-[17px] w-[17px] stroke-[1.9]" />
          </div>
          <div>
            <h3 className="text-[14px] font-bold tracking-tight text-slate-900">
              Abastecimentos no período (setor)
            </h3>
          </div>
        </div>
      </div>
      {filteredFuelings.length === 0 ? (
        <div className="p-5">
          <EmptyState message="Nenhum abastecimento encontrado para o período selecionado." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Veículo</th>
                <th className="px-4 py-3 font-medium">Tipo</th>
                <th className="px-4 py-3 font-medium">Quantidade</th>
                <th className="px-4 py-3 font-medium">Valor/L</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Data</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filteredFuelings.map((fueling) => (
                <tr key={fueling.id}>
                  <td className="px-4 py-3 text-slate-600">
                    {getVehicleLabel(vehicleMap.get(fueling.vehicle_id))}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fueling.fuel_type || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{fueling.amount?.toFixed(1) || 0} L</td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatCurrency(fueling.cost_per_liter || 0)}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {formatCurrency(fueling.total_cost || 0)}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateValue(fueling.fueling_time || fueling.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderMaintenancesTab = () => (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)]">
      <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50/80 to-white px-5 py-4 ring-1 ring-inset ring-white/60">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
            <Wrench className="h-[17px] w-[17px] stroke-[1.9]" />
          </div>
          <div>
            <h3 className="text-[14px] font-bold tracking-tight text-slate-900">
              Manutenções no período (setor)
            </h3>
          </div>
        </div>
      </div>
      {filteredMaintenances.length === 0 ? (
        <div className="p-5">
          <EmptyState message="Nenhuma manutenção encontrada para o período selecionado." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Veículo</th>
                <th className="px-4 py-3 font-medium">Motivo</th>
                <th className="px-4 py-3 font-medium">Entrada</th>
                <th className="px-4 py-3 font-medium">Previsão</th>
                <th className="px-4 py-3 font-medium">Custo</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filteredMaintenances.map((maintenance) => (
                <tr key={maintenance.id}>
                  <td className="px-4 py-3 text-slate-600">
                    {getVehicleLabel(vehicleMap.get(maintenance.vehicle_id))}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{maintenance.reason}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateValue(maintenance.entry_date)}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateValue(maintenance.estimated_completion)}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {formatCurrency(maintenance.final_price || 0)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${getStatusChipClass(maintenance.status)}`}
                    >
                      {maintenanceStatusLabelMap[maintenance.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderIncidentsTab = () => (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)]">
      <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50/80 to-white px-5 py-4 ring-1 ring-inset ring-white/60">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200">
            <AlertTriangle className="h-[17px] w-[17px] stroke-[1.9]" />
          </div>
          <div>
            <h3 className="text-[14px] font-bold tracking-tight text-slate-900">
              Incidentes no período (setor)
            </h3>
          </div>
        </div>
      </div>
      {filteredIncidents.length === 0 ? (
        <div className="p-5">
          <EmptyState message="Nenhum incidente encontrado para o período selecionado." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Veículo</th>
                <th className="px-4 py-3 font-medium">Data</th>
                <th className="px-4 py-3 font-medium">Hora</th>
                <th className="px-4 py-3 font-medium">Local</th>
                <th className="px-4 py-3 font-medium">Descrição</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filteredIncidents.map((incident) => (
                <tr key={incident.id}>
                  <td className="px-4 py-3 text-slate-600">
                    {getVehicleLabel(vehicleMap.get(incident.vehicle_id))}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDateValue(incident.date)}</td>
                  <td className="px-4 py-3 text-slate-600">{incident.time || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{incident.location || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{incident.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderPostUseReportsTab = () => {
    const pendingCount = concludedTripsInRange.filter(
      (t) => !postUseReportsByTripId.has(t.id),
    ).length;

    return (
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)] ring-1 ring-inset ring-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-12px_rgba(15,23,42,0.2)]">
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-slate-50 transition-all duration-200 group-hover:bg-slate-100" />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 text-white shadow-[0_4px_14px_-4px_rgba(51,65,85,0.55)] ring-1 ring-inset ring-white/25">
                <BadgeCheck className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <span className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-600 ring-1 ring-inset ring-slate-200">
                Total
              </span>
            </div>
            <div className="relative mt-4">
              <p className="text-[12.5px] font-semibold leading-tight text-slate-500">
                Viagens concluídas (setor)
              </p>
              <p className="mt-1.5 text-2xl font-bold leading-tight tracking-tight text-slate-900">
                {concludedTripsInRange.length}
              </p>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)] ring-1 ring-inset ring-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-12px_rgba(16,185,129,0.25)]">
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-emerald-50 transition-all duration-200 group-hover:bg-emerald-100/60" />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-[0_4px_14px_-4px_rgba(16,185,129,0.55)] ring-1 ring-inset ring-white/25">
                <FileCheck2 className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-emerald-700 ring-1 ring-inset ring-emerald-200">
                OK
              </span>
            </div>
            <div className="relative mt-4">
              <p className="text-[12.5px] font-semibold leading-tight text-slate-500">
                Relatórios preenchidos
              </p>
              <p className="mt-1.5 text-2xl font-bold leading-tight tracking-tight text-emerald-700">
                {concludedTripsInRange.length - pendingCount}
              </p>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)] ring-1 ring-inset ring-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-12px_rgba(217,119,6,0.28)]">
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-50 transition-all duration-200 group-hover:bg-amber-100/70" />
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-[0_4px_14px_-4px_rgba(217,119,6,0.55)] ring-1 ring-inset ring-white/25">
                <FileWarning className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-amber-800 ring-1 ring-inset ring-amber-200">
                Ação
              </span>
            </div>
            <div className="relative mt-4">
              <p className="text-[12.5px] font-semibold leading-tight text-slate-500">
                Pendentes de preenchimento
              </p>
              <p className="mt-1.5 text-2xl font-bold leading-tight tracking-tight text-amber-700">
                {pendingCount}
              </p>
            </div>
          </div>
        </div>

        {concludedTripsInRange.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)] ring-1 ring-inset ring-slate-50">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50 text-slate-500 shadow-[0_2px_6px_-1px_rgba(15,23,42,0.05)] ring-1 ring-inset ring-slate-200">
              <ClipboardList className="h-6 w-6 stroke-[1.8]" />
            </div>
            <p className="text-[13.5px] font-semibold leading-snug text-slate-700">
              Nenhuma viagem concluída encontrada para o período selecionado.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.2fr_1fr]">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)]">
              <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50/80 to-white px-5 py-4 ring-1 ring-inset ring-white/60">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200">
                    <Activity className="h-[17px] w-[17px] stroke-[1.9]" />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-bold tracking-tight text-slate-900">
                      Viagens concluídas
                    </h3>
                    <p className="mt-0.5 text-[12px] font-medium leading-tight text-slate-500">
                      Clique em &quot;Visualizar&quot; para conferir o relatório de pós-uso.
                    </p>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-gradient-to-b from-slate-50 to-white text-left uppercase tracking-[0.08em]">
                    <tr>
                      <th className="px-4 py-3.5 text-[11.5px] font-bold text-slate-600">ID</th>
                      <th className="px-4 py-3.5 text-[11.5px] font-bold text-slate-600">
                        Destino / Motivo
                      </th>
                      <th className="px-4 py-3.5 text-[11.5px] font-bold text-slate-600">
                        Veículo
                      </th>
                      <th className="px-4 py-3.5 text-[11.5px] font-bold text-slate-600">
                        Motorista
                      </th>
                      <th className="px-4 py-3.5 text-[11.5px] font-bold text-slate-600">Fim</th>
                      <th className="px-4 py-3.5 text-[11.5px] font-bold text-slate-600">
                        KM final
                      </th>
                      <th className="px-4 py-3.5 text-[11.5px] font-bold text-slate-600">Status</th>
                      <th className="px-4 py-3.5 text-[11.5px] font-bold text-slate-600"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {concludedTripsInRange.map((trip) => {
                      const report = postUseReportsByTripId.get(trip.id);
                      const isSelected = trip.id === selectedPostUseTripId;
                      const emp = employeeMap.get(trip.employee_id);
                      return (
                        <tr
                          key={trip.id}
                          className={`transition-colors duration-100 ${isSelected ? 'bg-blue-50/60 ring-1 ring-inset ring-blue-100' : 'hover:bg-slate-50/60'}`}
                        >
                          <td className="whitespace-nowrap px-4 py-3.5">
                            <span className="inline-flex items-center rounded-md bg-slate-100/80 px-2 py-0.5 text-[13px] font-bold leading-tight text-slate-700 ring-1 ring-inset ring-slate-200/60">
                              #{trip.id}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 min-w-0 max-w-[220px]">
                            <div
                              className="truncate text-[13.5px] font-bold leading-tight text-slate-800"
                              title={trip.destination || ''}
                            >
                              {trip.destination || '-'}
                            </div>
                            <div
                              className="mt-0.5 truncate text-[12px] font-medium leading-snug text-slate-500 line-clamp-1"
                              title={trip.purpose || ''}
                            >
                              {trip.purpose || '-'}
                            </div>
                          </td>
                          <td
                            className="px-4 py-3.5 text-[13px] font-semibold leading-snug text-slate-700 truncate max-w-[160px]"
                            title={tripVehicleLabel(trip)}
                          >
                            <span className="inline-flex items-center gap-1.5">
                              <Car className="h-3.5 w-3.5 text-slate-400" />
                              {tripVehicleLabel(trip)}
                            </span>
                          </td>
                          <td
                            className="px-4 py-3.5 text-[13px] font-semibold leading-snug text-slate-700 truncate max-w-[160px]"
                            title={emp?.name || ''}
                          >
                            <span className="inline-flex items-center gap-1.5">
                              <User className="h-3.5 w-3.5 text-slate-400" />
                              {emp?.name || `Funcionário #${trip.employee_id}`}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3.5 text-[12.5px] font-semibold leading-snug text-slate-600">
                            <span className="inline-flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5 text-slate-400" />
                              {formatTripEndDateTime(trip.end_date || trip.updated_at)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3.5 text-[13px] font-bold leading-tight text-slate-800">
                            <span className="inline-flex items-center gap-1.5">
                              <Gauge className="h-3.5 w-3.5 text-slate-400" />
                              {typeof trip.final_mileage === 'number'
                                ? trip.final_mileage.toLocaleString('pt-BR')
                                : '-'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            {report ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11.5px] font-bold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                                <CheckCircle2 className="h-3.5 w-3.5 stroke-[2]" />
                                Preenchido
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11.5px] font-bold text-amber-800 ring-1 ring-inset ring-amber-600/20">
                                <TimerReset className="h-3.5 w-3.5 stroke-[2]" />
                                Pendente
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right">
                            <Button
                              type="button"
                              onClick={() => setSelectedPostUseTripId(trip.id)}
                              variant={isSelected ? 'primary' : 'secondary'}
                              size="sm"
                              className="inline-flex"
                            >
                              <Eye className="h-3.5 w-3.5 stroke-[1.9]" />
                              Visualizar
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_0_rgba(15,23,42,0.04),0_1px_3px_0_rgba(15,23,42,0.04)]">
              <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50/80 to-white px-5 py-4 ring-1 ring-inset ring-white/60">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ${selectedPostUseReport ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-50 text-slate-500 ring-slate-200'}`}
                    >
                      <FileText className="h-[17px] w-[17px] stroke-[1.9]" />
                    </div>
                    <div>
                      <h3 className="text-[14px] font-bold tracking-tight text-slate-900">
                        {selectedPostUseReport ? 'Relatório de pós-uso' : 'Selecione uma viagem'}
                      </h3>
                      {selectedPostUseReport && (
                        <p className="mt-0.5 text-[12px] font-medium leading-tight text-slate-500">
                          Atualizado em{' '}
                          {formatTripEndDateTime(selectedPostUseReport.updated_at)}
                        </p>
                      )}
                    </div>
                  </div>
                  {selectedPostUseReport && (
                    <span className="mt-0.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-700 ring-1 ring-inset ring-emerald-200">
                      <BadgeCheck className="h-3.5 w-3.5 stroke-[2]" />
                      Finalizado
                    </span>
                  )}
                </div>
              </div>
              {selectedPostUseTrip ? (
                <div className="flex flex-col gap-5 p-5 bg-slate-50/20">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-[0_1px_2px_0_rgba(15,23,42,0.03)] ring-1 ring-inset ring-slate-50 transition-all hover:ring-blue-100">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-100">
                          <MapPin className="h-[15px] w-[15px] stroke-[2]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                            Viagem
                          </p>
                          <p className="mt-0.5 text-[14px] font-bold leading-tight text-slate-900">
                            #{selectedPostUseTrip.id}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-[0_1px_2px_0_rgba(15,23,42,0.03)] ring-1 ring-inset ring-slate-50 transition-all hover:ring-purple-100">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-100">
                          <Clock3 className="h-[15px] w-[15px] stroke-[2]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                            Tempo de uso
                          </p>
                          <p className="mt-0.5 text-[14px] font-bold leading-tight text-slate-900">
                            {formatTripMinutes(selectedPostUseTrip.usage_duration_minutes)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-[0_1px_2px_0_rgba(15,23,42,0.03)] ring-1 ring-inset ring-slate-50 transition-all hover:ring-rose-100">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-100">
                          <User className="h-[15px] w-[15px] stroke-[2]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                            Motorista
                          </p>
                          <p
                            className="mt-0.5 truncate text-[13.5px] font-bold leading-tight text-slate-900"
                            title={employeeMap.get(selectedPostUseTrip.employee_id)?.name || ''}
                          >
                            {employeeMap.get(selectedPostUseTrip.employee_id)?.name ||
                              `Funcionário #${selectedPostUseTrip.employee_id}`}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-[0_1px_2px_0_rgba(15,23,42,0.03)] ring-1 ring-inset ring-slate-50 transition-all hover:ring-emerald-100">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100">
                          <Car className="h-[15px] w-[15px] stroke-[2]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                            Veículo
                          </p>
                          <p
                            className="mt-0.5 truncate text-[13.5px] font-bold leading-tight text-slate-900 line-clamp-1"
                            title={tripVehicleLabel(selectedPostUseTrip)}
                          >
                            {tripVehicleLabel(selectedPostUseTrip)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_2px_8px_-3px_rgba(15,23,42,0.08)] ring-1 ring-inset ring-slate-50">
                    <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white px-5 py-3.5 ring-1 ring-inset ring-white">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200">
                          <FileText className="h-[15px] w-[15px] stroke-[2]" />
                        </div>
                        <label className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-slate-600">
                          Descrição do pós-uso
                        </label>
                      </div>
                    </div>
                    <div className="min-h-[260px] whitespace-pre-wrap px-5 py-4 text-[13.5px] leading-relaxed text-slate-800">
                      {selectedPostUseReport?.content?.trim() || (
                        <div className="flex h-full min-h-[220px] flex-col items-center justify-center gap-3 text-center py-10">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50 text-slate-400 ring-1 ring-inset ring-slate-200">
                            <FileWarning className="h-6 w-6 stroke-[1.7]" />
                          </div>
                          <p className="text-[13px] italic font-medium leading-snug text-slate-400 max-w-sm mx-auto">
                            Nenhum relatório de pós-uso preenchido para esta viagem.
                          </p>
                        </div>
                      )}
                    </div>
                    {selectedPostUseReport?.content?.trim() ? null : (
                      <div className="border-t border-amber-100 bg-gradient-to-br from-amber-50/80 to-orange-50/60 px-5 py-4 ring-1 ring-inset ring-amber-100/60">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-white px-4 py-2.5 text-[12.5px] font-bold text-amber-800 shadow-[0_1px_2px_0_rgba(217,119,6,0.06)] ring-1 ring-inset ring-white transition-all hover:bg-amber-50 hover:border-amber-300"
                        >
                          <AlertTriangle className="h-[17px] w-[17px] stroke-[1.9]" />
                          Motorista ainda não preencheu o relatório
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-4 py-14 px-8 text-center bg-slate-50/20">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-blue-500/10 blur-xl" />
                    <div className="relative flex h-20 w-20 items-center justify-center rounded-3xl border border-slate-200 bg-white shadow-[0_10px_30px_-12px_rgba(59,130,246,0.25)] ring-1 ring-inset ring-white">
                      <MousePointer2 className="h-10 w-10 stroke-[1.6] text-blue-600" />
                    </div>
                  </div>
                  <div className="space-y-1.5 max-w-xs">
                    <h4 className="text-[15px] font-bold tracking-tight text-slate-900">
                      Nenhuma viagem selecionada
                    </h4>
                    <p className="text-[13px] font-medium leading-snug text-slate-500">
                      Selecione uma viagem na lista ao lado para visualizar o relatório.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 0:
        return renderOverviewTab();
      case 1:
        return renderVehiclesTab();
      case 2:
        return renderTripsTab();
      case 3:
        return renderFuelingsTab();
      case 4:
        return renderMaintenancesTab();
      case 5:
        return renderIncidentsTab();
      case 6:
        return renderPostUseReportsTab();
      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Relatórios do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Relatórios — Setor
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Dados consolidados do seu departamento
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                no período selecionado
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <SectionCard>
            <CardHeader>
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                    Ações rápidas
                  </h2>
                  <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                    Exporte em PDF ou alterne entre visão resumida e detalhada
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={generatePDF} variant="primary">
                    <Download className="h-4 w-4 stroke-[1.9]" />
                    Exportar PDF
                  </Button>
                  <Button
                    onClick={() => setView(view === 'overview' ? 'detailed' : 'overview')}
                    variant="secondary"
                  >
                    {view === 'overview' ? (
                      <>
                        <Layers className="h-4 w-4 stroke-[1.9]" />
                        Mostrar detalhes extras
                      </>
                    ) : (
                      <>
                        <LayoutList className="h-4 w-4 stroke-[1.9]" />
                        Mostrar visão resumida
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
          </SectionCard>

          <SectionCard>
            <CardHeader>
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                    <CalendarRange className="h-[18px] w-[18px] stroke-[1.9]" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-bold uppercase tracking-[0.14em] text-slate-700">
                      Período do relatório
                    </h2>
                    <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                      Selecione o intervalo de datas para análise
                    </p>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3.5 py-2 ring-1 ring-inset ring-slate-200">
                  <CalendarRange className="h-[15px] w-[15px] text-slate-500" />
                  <span className="text-[12.5px] font-semibold text-slate-700">
                    {formatDateValue(dateRange.startDate)} — {formatDateValue(dateRange.endDate)}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="flex flex-wrap gap-2">
                {(['7days', '30days', '90days', 'custom'] as ReportPeriod[]).map(
                  (periodValue) => {
                    const isActive = period === periodValue;
                    return (
                      <button
                        key={periodValue}
                        onClick={() => handlePeriodChange(periodValue)}
                        className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-all ${
                          isActive
                            ? 'border-blue-600 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-[0_4px_14px_-6px_rgba(37,99,235,0.55)] ring-1 ring-inset ring-white/25'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
                        }`}
                      >
                        {periodValue === '7days' && 'Últimos 7 dias'}
                        {periodValue === '30days' && 'Últimos 30 dias'}
                        {periodValue === '90days' && 'Últimos 90 dias'}
                        {periodValue === 'custom' && 'Personalizado'}
                      </button>
                    );
                  },
                )}
              </div>

              {period === 'custom' && (
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <label className="block text-sm">
                    <span className="mb-1.5 block text-[12.5px] font-semibold uppercase tracking-wide text-slate-500">
                      Data inicial
                    </span>
                    <input
                      type="date"
                      value={formatDateForInput(dateRange.startDate)}
                      onChange={(e) => handleDateChange(e, 'start')}
                      className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 h-10"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="mb-1.5 block text-[12.5px] font-semibold uppercase tracking-wide text-slate-500">
                      Data final
                    </span>
                    <input
                      type="date"
                      value={formatDateForInput(dateRange.endDate)}
                      onChange={(e) => handleDateChange(e, 'end')}
                      className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 h-10"
                    />
                  </label>
                </div>
              )}
            </CardBody>
          </SectionCard>

          <SectionCard>
            <CardBody>
              <div className="flex min-w-max border-b border-slate-200">
                {tabs.map((label, index) => {
                  const isActive = activeTab === index;
                  return (
                    <button
                      key={label}
                      onClick={() => setActiveTab(index as ReportTab)}
                      className={`relative border-b-2 px-5 py-4 text-[13.5px] font-semibold transition-all ${
                        isActive
                          ? 'border-blue-600 text-blue-700'
                          : 'border-transparent text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      <span
                        className={`-mx-1 inline-flex items-center rounded-lg px-2.5 py-1 transition-colors ${
                          isActive ? 'bg-blue-50 text-blue-700' : ''
                        }`}
                      >
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="p-5">{renderActiveTab()}</div>
            </CardBody>
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

export default ClientRelatorios;
